import SparkMD5 from 'spark-md5';
import type { ApiClient } from '../apiClient';

/**
 * Production Traefik drops a request whose body takes over 60 s, so a file
 * above this size goes up as an S3 multipart upload in parts of (at most)
 * this size; smaller files keep the single presigned PUT
 * (docs/superpowers/specs/2026-10-12-multipart-direct-uploads-design.md §3).
 * The server's `part_size` from multipart/create is what parts are cut by.
 */
export const MULTIPART_PART_SIZE = 15 * 1024 * 1024;

const READ_SLICE = 2 * 1024 * 1024;
const URL_BATCH = 50;
const URL_MAX_AGE_MS = 20 * 60 * 1000;
const DEFAULT_RETRY_DELAYS_MS = [1000, 4000, 10000] as const;

export type UploadPhase = 'preparing' | 'uploading';

export interface UploadToStorageOptions {
  /** The upload resource, e.g. '/direct/picsvc/retouch_uploads'. */
  basePath: string;
  file: File;
  /** Defaults to file.type (omitted when the browser reports none). */
  contentType?: string;
  /** Parts in flight at once: 2 for a zip, 1 per file where files already run in parallel. */
  partConcurrency: number;
  onPhase?: (phase: UploadPhase) => void;
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
  /** Waits before each retry of a failed part; an option only so tests can shorten them. */
  retryDelaysMs?: readonly number[];
}

/** A storage PUT that failed; status 0 is a network error. */
export class StorageHttpError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(status ? `PUT ${status}` : 'network error');
    this.name = 'StorageHttpError';
    this.status = status;
  }
}

interface PresignResult { blob_signed_id: string; upload_url: string; headers: Record<string, string> }
interface MultipartCreateResult { blob_signed_id: string; upload_token: string; part_size: number; part_count: number }
interface PartUrl { url: string; headers: Record<string, string>; fetchedAt: number }

const abortError = () => new DOMException('aborted', 'AbortError');

/**
 * The whole-file MD5 the presign wants and, for a file over `partSize`, the
 * MD5 of every part, from one streaming read: slices end at the next 2 MiB
 * step or part boundary, whichever comes first, so none spans two parts.
 */
export const computeUploadDigests = async (
  file: File,
  partSize: number,
  signal?: AbortSignal,
): Promise<{ checksum: string; partMd5s: string[] }> => {
  const whole = new SparkMD5.ArrayBuffer();
  const split = file.size > partSize;
  const partMd5s: string[] = [];
  let part = new SparkMD5.ArrayBuffer();
  let offset = 0;
  while (offset < file.size) {
    signal?.throwIfAborted();
    const partEnd = (Math.floor(offset / partSize) + 1) * partSize;
    const end = Math.min(offset + READ_SLICE, partEnd, file.size);
    const buf = await file.slice(offset, end).arrayBuffer();
    whole.append(buf);
    if (split) part.append(buf);
    offset = end;
    if (split && (offset === partEnd || offset === file.size)) {
      partMd5s.push(btoa(part.end(true)));
      part = new SparkMD5.ArrayBuffer();
    }
  }
  signal?.throwIfAborted();
  return { checksum: btoa(whole.end(true)), partMd5s };
};

// One presigned PUT. Resolves with the response's ETag (null when storage
// sends none or CORS hides it). onBytes reports bytes of `body` sent so far.
const putBody = (
  url: string,
  headers: Record<string, string>,
  body: Blob,
  signal: AbortSignal,
  onBytes: (sent: number) => void,
): Promise<string | null> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable && e.total > 0) onBytes((e.loaded / e.total) * body.size);
    };
    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    const settle = () => signal.removeEventListener('abort', onAbort);
    xhr.onload = () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag'));
      else reject(new StorageHttpError(xhr.status));
    };
    xhr.onerror = () => { settle(); reject(new StorageHttpError(0)); };
    xhr.onabort = () => { settle(); reject(abortError()); };
    xhr.send(body);
  });

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve(); }, ms);
    const onAbort = () => { clearTimeout(timer); reject(abortError()); };
    signal.addEventListener('abort', onAbort, { once: true });
  });

const isRetryable = (status: number) => status === 0 || status === 408 || status === 429 || status >= 500;

/**
 * Hash → presign → PUT for a file up to MULTIPART_PART_SIZE; hash →
 * multipart/create → parts (with retry) → multipart/complete above it.
 * Resolves with the blob the caller's confirm takes. On cancel or failure a
 * multipart upload is aborted (best effort) before the promise rejects; a
 * cancel rejects with an AbortError.
 */
export const uploadToStorage = async (
  api: ApiClient,
  opts: UploadToStorageOptions,
): Promise<{ blob_signed_id: string }> => {
  const { basePath, file, partConcurrency, onPhase, onProgress, signal } = opts;
  const retryDelays = opts.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS;
  const contentType = opts.contentType ?? (file.type || undefined);
  const report = (bytes: number) => onProgress?.(file.size ? Math.min(100, Math.round((bytes / file.size) * 100)) : 100);

  onPhase?.('preparing');
  const digests = await computeUploadDigests(file, MULTIPART_PART_SIZE, signal);
  const meta = { filename: file.name, byte_size: file.size, checksum: digests.checksum, content_type: contentType };

  if (file.size <= MULTIPART_PART_SIZE) {
    const presign = await api.post<PresignResult>(`${basePath}/presign`, meta);
    signal?.throwIfAborted();
    onPhase?.('uploading');
    await putBody(presign.upload_url, presign.headers, file, signal ?? new AbortController().signal, report);
    report(file.size);
    return { blob_signed_id: presign.blob_signed_id };
  }

  const created = await api.post<MultipartCreateResult>(`${basePath}/multipart`, meta);
  const token = created.upload_token;
  // Inner signal: the first part to fail for good stops the others.
  const inner = new AbortController();
  const forward = () => inner.abort();
  signal?.addEventListener('abort', forward, { once: true });
  try {
    signal?.throwIfAborted();
    const partSize = created.part_size;
    const partMd5s = partSize === MULTIPART_PART_SIZE
      ? digests.partMd5s
      : (await computeUploadDigests(file, partSize, inner.signal)).partMd5s;
    const count = partMd5s.length;
    onPhase?.('uploading');

    const urls = new Map<number, PartUrl>();
    const pendingBatches = new Map<number, Promise<void>>();
    const fetchUrls = async (numbers: number[]) => {
      const res = await api.post<{ parts: Array<{ part_number: number; url: string; headers: Record<string, string> }> }>(
        `${basePath}/multipart/parts`,
        { upload_token: token, parts: numbers.map((n) => ({ part_number: n, md5: partMd5s[n - 1] })) },
      );
      const fetchedAt = Date.now();
      res.parts.forEach((p) => urls.set(p.part_number, { url: p.url, headers: p.headers, fetchedAt }));
    };
    // URLs for parts n..end of n's batch of 50 (its first use, or a refresh once
    // n's URL is stale), one request in flight per batch however many workers wait.
    const fetchBatchFrom = (n: number) => {
      const first = Math.floor((n - 1) / URL_BATCH) * URL_BATCH + 1;
      let pending = pendingBatches.get(first);
      if (!pending) {
        const last = Math.min(first + URL_BATCH - 1, count);
        pending = fetchUrls(Array.from({ length: last - n + 1 }, (_, i) => n + i))
          .finally(() => pendingBatches.delete(first));
        pendingBatches.set(first, pending);
      }
      return pending;
    };
    const usable = (u: PartUrl | undefined) => !!u && Date.now() - u.fetchedAt < URL_MAX_AGE_MS;
    const urlFor = async (n: number, fresh: boolean): Promise<PartUrl> => {
      if (fresh) await fetchUrls([n]);
      // A second round when the request we awaited was another worker's that did not cover n.
      for (let round = 0; round < 2 && !usable(urls.get(n)); round += 1) await fetchBatchFrom(n);
      const url = urls.get(n);
      if (!url) throw new Error(`no URL for part ${n}`);
      return url;
    };

    const sentBytes = new Array<number>(count).fill(0);
    const emit = () => report(sentBytes.reduce((a, b) => a + b, 0));
    const etags = new Array<string>(count);

    const uploadPart = async (n: number) => {
      const body = file.slice((n - 1) * partSize, Math.min(n * partSize, file.size));
      let retries = 0;
      let refreshed = false;
      let fresh = false;
      for (;;) {
        inner.signal.throwIfAborted();
        const { url, headers } = await urlFor(n, fresh);
        fresh = false;
        try {
          const etag = await putBody(url, headers, body, inner.signal, (b) => { sentBytes[n - 1] = b; emit(); });
          if (!etag) throw new Error('storage sent no ETag for the part (check the bucket CORS ExposeHeaders)');
          sentBytes[n - 1] = body.size;
          emit();
          etags[n - 1] = etag;
          return;
        } catch (e) {
          sentBytes[n - 1] = 0;
          emit();
          if (!(e instanceof StorageHttpError)) throw e;
          // An expired URL: Garage answers 400 "Date is too old", S3 403.
          if ((e.status === 400 || e.status === 403) && !refreshed) {
            refreshed = true;
            fresh = true;
            continue;
          }
          if (isRetryable(e.status) && retries < retryDelays.length) {
            await sleep(retryDelays[retries], inner.signal);
            retries += 1;
            continue;
          }
          throw e;
        }
      }
    };

    let next = 1;
    const worker = async () => {
      while (next <= count) {
        const n = next;
        next += 1;
        await uploadPart(n);
      }
    };
    await Promise.all(Array.from({ length: Math.max(1, Math.min(partConcurrency, count)) }, worker)).catch((e) => {
      inner.abort();
      throw e;
    });

    await api.post(`${basePath}/multipart/complete`, {
      upload_token: token,
      parts: etags.map((etag, i) => ({ part_number: i + 1, etag })),
    });
    return { blob_signed_id: created.blob_signed_id };
  } catch (e) {
    void api.post(`${basePath}/multipart/abort`, { upload_token: token }).catch(() => undefined);
    throw signal?.aborted ? abortError() : e;
  } finally {
    signal?.removeEventListener('abort', forward);
  }
};
